import { JSONSchema7Definition } from 'json-schema';
import { defaults as _defaults } from 'lodash';
import {
    FormElement,
    FormElementConfig,
    formElementDefaultConfig,
} from '../formElement';

export interface FormFieldConfig<TValue> extends FormElementConfig {
    value?: TValue;
    /*
        In some cases where the generic form field type is used (e.g. hidden backing fields),
        determining the exact type at runtime is hard or even impossible without implementing hard checks against various types, kinda "breaking" the generic'ness of the base type,
        so we opt to being able to hard configure the schema instead
    */
    jsonSchema?: JSONSchema7Definition;
}

export const formFieldDefaultConfig = {
    ...formElementDefaultConfig,
    value: null,
    excludeFromSchema: false,
};

export class FormField<TValue> extends FormElement {
    public value: TValue | null;
    private jsonSchema: JSONSchema7Definition | null;

    public constructor(config?: FormFieldConfig<TValue>) {
        super(config);
        const resolvedConfig = _defaults(
            config || ({} as FormFieldConfig<TValue>),
            formFieldDefaultConfig,
        );

        super(resolvedConfig);
        this.value = resolvedConfig.value ?? null;
        this.jsonSchema = resolvedConfig.jsonSchema ?? null;
    }

    public getJsonSchema(): JSONSchema7Definition {
        if (this.jsonSchema !== null) {
            return this.jsonSchema;
        }

        const type = typeof this.value;
        if (this.value === null) {
            // A workaround. Currently, we only return string type for null values.
            return {
                type: 'string',
            };
        }

        switch (type) {
            case 'number':
            case 'boolean':
            case 'string':
                return {
                    type,
                };
        }

        return {};
    }

    public isValid(): boolean {
        return Object.values(this.validators).every((validator) => {
            if (typeof validator === 'function') {
                return validator(this.value);
            }
            return false;
        });
    }
}
